import { Button } from '@/components/UI/Button'
import { IconChevronLeft } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './PhotoFrame.module.css'
import { getMobileOS } from './helpers'

type ConfirmationModalProps = {
  show: boolean
  mediaType: 'video' | 'image'
  dataSrc: string | null | undefined
  onClose: () => void
  onSave: () => void
  recordingDuration?: number
}

export const ConfirmationModal = ({
  show,
  mediaType,
  dataSrc,
  onClose,
  onSave,
  recordingDuration,
}: ConfirmationModalProps) => {
  const { t } = useTranslation()
  const mobileOS = getMobileOS()
  const [videoError, setVideoError] = useState<string | null>(null)

  const previewVideoRef = useRef<HTMLVideoElement | null>(null)
  const isVideo = mediaType === 'video'

  // biome-ignore lint/correctness/useExhaustiveDependencies: previewVideoRef.current を依存に含め、video 要素がマウントされた後にイベントリスナーを張り直す
  useEffect(() => {
    if (!previewVideoRef.current || !isVideo || !dataSrc) {
      return
    }

    const video = previewVideoRef.current
    video.load()

    const handleError = (e: Event) => {
      console.error('Video error:', e)
      const target = e.target as HTMLVideoElement
      const error = target.error
      if (error) {
        console.error('Video error details:', {
          code: error.code,
          message: error.message,
          MEDIA_ERR_ABORTED: error.MEDIA_ERR_ABORTED,
          MEDIA_ERR_NETWORK: error.MEDIA_ERR_NETWORK,
          MEDIA_ERR_DECODE: error.MEDIA_ERR_DECODE,
          MEDIA_ERR_SRC_NOT_SUPPORTED: error.MEDIA_ERR_SRC_NOT_SUPPORTED,
        })
      }
      setVideoError(t('spot_detail.ar.video_load_error', '動画の読み込みに失敗しました'))
    }

    const handleCanPlay = () => {
      setVideoError(null)
      if (mobileOS === 'Android') return
      video.play().catch((error) => {
        console.warn('Video play failed:', error)
      })
    }

    const handleLoadStart = () => setVideoError(null)

    const handleLoadedMetadata = () => {
      const videoDuration = video.duration
      if (Number.isFinite(videoDuration)) {
        // 最終フレームのサムネイル生成のため末尾にシーク
        video.currentTime = videoDuration
        video.onseeked = () => {
          video.onseeked = null
        }
      }
    }

    video.addEventListener('error', handleError)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('loadstart', handleLoadStart)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)

    return () => {
      video.removeEventListener('error', handleError)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('loadstart', handleLoadStart)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [previewVideoRef.current, isVideo, dataSrc, mobileOS])

  const confirmationText = t('spot_detail.ar.save_notification', '撮影した{{what}}を保存しますか？', {
    what: isVideo ? t('spot_detail.ar.video', 'ビデオ') : t('spot_detail.ar.photo', '写真'),
  })

  const renderMedia = () => {
    if (!dataSrc) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400">
          {t('spot_detail.ar.no_media_data', 'メディアデータがありません')}
        </div>
      )
    }

    if (isVideo) {
      return (
        <>
          <video
            ref={previewVideoRef}
            className="w-full h-full object-contain"
            src={dataSrc}
            controls
            autoPlay={mobileOS === 'iOS'}
            playsInline
            muted={mobileOS === 'iOS'}
            preload="metadata"
            crossOrigin="anonymous"
            onError={(e) => {
              console.error('Video element error:', e)
              setVideoError(t('spot_detail.ar.video_load_error', '動画の読み込みに失敗しました'))
            }}
            onLoadStart={() => setVideoError(null)}
            onCanPlay={() => setVideoError(null)}
          >
            {t('spot_detail.ar.video_not_supported', 'お使いのブラウザは動画の再生をサポートしていません。')}
          </video>
          {videoError && <div className="text-red-500 text-sm mt-2">{videoError}</div>}
        </>
      )
    }

    return (
      <img
        src={dataSrc}
        className="w-full h-full object-contain"
        alt={t('spot_detail.ar.saved_image_alt', '保存画像')}
      />
    )
  }

  if (!show) return <></>

  return (
    <div className="fixed inset-0 z-[100] bg-[#FFFAE6] flex flex-col">
      {/* タイトル */}
      <p className="font-bold text-[18px] text-black text-center px-4 pt-[20px] pb-[12px] flex-shrink-0">
        {confirmationText}
      </p>

      {/* メディアプレビュー */}
      <div className="flex-1 overflow-hidden flex items-center justify-center bg-[#FFFAE6]">{renderMedia()}</div>

      {/* ボタン行: 撮り直す | 保存する */}
      <div className="flex justify-center gap-4 py-6 px-4 flex-shrink-0">
        <Button
          label={t('spot_detail.ar.re_take', '撮り直す')}
          variant="system-back"
          size="md"
          onClick={onClose}
          fullWidth
        />
        <Button
          label={t('spot_detail.ar.to_save', '保存する')}
          variant="primary"
          size="md"
          onClick={onSave}
          fullWidth
        />
      </div>
    </div>
  )
}
